import { Injectable, computed, inject, signal, WritableSignal, effect, ApplicationRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, first, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
// import { Credentials, TokenData } from '../../../shared/models/auth.interface';
// import { MatDialog } from '@angular/material/dialog';
import { StorageService } from '../storage/storage.service';
import { IUserResponse, IUser } from '../../../shared/models/user.interface';
import { environment } from '../../../../environments/environment';
import { IAuthData } from '../../models/auth-data.interface';
import { NotificationService } from '../../../shared/services/notification/notification.service';
import { IRegisterUser, IRegisterUserAuthData, IRegisterUserResponse } from '../../models/register-user.interface';
import { AuthState, Token } from '../../models/auth-state.interface';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storageService = inject(StorageService);
  private notificationService = inject(NotificationService);
  private applicationRef = inject(ApplicationRef);
  private http = inject(HttpClient);

  private readonly AUTH_TOKEN_KEY = 'authToken';
  private readonly EXP_TOKEN_KEY = 'expToken';
  // private readonly REFRESH_INTERVAL_MS = (4 * 60 + 50) * 1000; // 4 minutes 50 seconds

  private authState: WritableSignal<AuthState> = signal<AuthState>({
    token: this.getAuthToken(),
    expToken: this.getExpToken(),
  });
  public authTokenData = computed(() => this.authState());

  // public isTokenValid = computed(() => {
  //   const token = this.getAuthToken();
  //   const expToken = this.getExpToken();

  //   if (token && expToken) {
  //     const currentTime = Math.floor(Date.now() / 1000);
  //     const tokenExpirationDate = Math.floor(new Date(expToken).getTime() / 1000);

  //     if (token && tokenExpirationDate <= currentTime) {
  //       return false;
  //     }

  //     return true;
  //   }

  //   return false;
  // });

  private currentUserSubject = new BehaviorSubject<IUser | undefined>(undefined);
  public currentUser$ = this.currentUserSubject.asObservable();

  public get currentUserValue(): IUser | undefined {
    return this.currentUserSubject.value;
  }

  public updateCurrentUser(user: IUser): void {
    return this.currentUserSubject.next(user);
  }

  public currentUserSignal = toSignal(this.currentUser$);

  public isCurrentUserLicenceActive = computed(() => {
    const user = this.currentUserSignal();
    if (user && user.end_subscription) {
      const now = new Date();
      const endLicenceDate = new Date(user.end_subscription);
      return endLicenceDate >= now;
    }
    return false;
  });

  // public isTokenValid(authToken: string, expToken: string): boolean {
  //   // const authToken = this.authToken();
  //   // const expToken = this.expToken();

  //   if (authToken && expToken) {
  //     const currentTime = Math.floor(Date.now() / 1000);
  //     const tokenExpirationDate = Math.floor(new Date(expToken).getTime() / 1000);

  //     if (authToken && tokenExpirationDate <= currentTime) {
  //       return false;
  //     }

  //     return true;
  //   }

  //   return false;
  // }

  // timerEffect = effect(onCleanup => {
  //   const token = this.authTokens().token;
  //   const refreshToken = this.authTokens().refreshToken;
  //   if (token && refreshToken) {
  //     this.applicationRef.isStable.pipe(first(isStable => isStable)).subscribe(() => {
  //       const timerId = setInterval(() => {
  //         this.refreshToken().subscribe();
  //       }, this.REFRESH_INTERVAL_MS);
  //       onCleanup(() => {
  //         clearInterval(timerId);
  //       });
  //     });
  //   }
  // });

  public login(email: string, password: string): Observable<IAuthData> {
    return this.http.post<IAuthData>(`${environment.apiAuth}/login`, { email, password }).pipe(
      tap((authData: IAuthData) => {
        if (authData.token && authData.expires_at) {
          this.storageService.setItem(this.AUTH_TOKEN_KEY, authData.token);
          this.storageService.setItem(this.EXP_TOKEN_KEY, authData.expires_at);
        }
        this.authState.set({ token: authData.token, expToken: authData.expires_at });
      }),
    );
  }

  public getUserData(): Observable<IUser> {
    return this.http.get<IUserResponse>(`${environment.apiUrl}/get-user-info?include=avatar`).pipe(
      map(response => response.data),
      tap(user => {
        this.currentUserSubject.next(user);
      }),
      catchError(error => {
        this.sessionExpired();
        throw error;
      }),
    );
  }

  public logout(message = 'Wylogowano pomyślnie'): void {
    this.clearLocalStorage();
    this.authState.set({ token: null, expToken: null });
    this.currentUserSubject.next(undefined);
    this.notificationService.success(message);
  }

  public sessionExpired(): void {
    this.clearLocalStorage();
    this.authState.set({ token: null, expToken: null });
    this.currentUserSubject.next(undefined);
  }

  public getAuthToken(): string | null {
    return this.storageService.getItem(this.AUTH_TOKEN_KEY);
  }

  public getExpToken(): string | null {
    return this.storageService.getItem(this.EXP_TOKEN_KEY);
  }

  public clearLocalStorage(): void {
    this.storageService.removeItem(this.AUTH_TOKEN_KEY);
    this.storageService.removeItem(this.EXP_TOKEN_KEY);
  }

  public register(data: IRegisterUser): Observable<IRegisterUserAuthData> {
    return this.http.post<IRegisterUserResponse>(`${environment.apiAuth}/register`, data).pipe(
      map(response => response.data),
      tap((authData: IRegisterUserAuthData) => {
        if (authData.token && authData.expires_at) {
          this.storageService.setItem(this.AUTH_TOKEN_KEY, authData.token);
          this.storageService.setItem(this.EXP_TOKEN_KEY, authData.expires_at);
        }
        this.authState.set({ token: authData.token, expToken: authData.expires_at });
      }),
    );
  }

  public forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${environment.apiAuth}/forgot-password`, { email });
  }

  public resetPassword(
    token: string,
    email: string,
    password: string,
    password_confirmation: string,
  ): Observable<void> {
    return this.http.post<void>(`${environment.apiAuth}/reset-password`, {
      token,
      email,
      password,
      password_confirmation,
    });
  }
}
